import React, { useState } from 'react';
import { useMsal } from "@azure/msal-react"; // Import useMsal
import { authFetch, isInteractiveAuthError } from '../authFetch';

function Visualise() {
  const { accounts } = useMsal(); // Get MSAL accounts
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [flightName, setFlightName] = useState('');
  const [flightLocation, setFlightLocation] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]); // Changed to handle multiple files

  const handleOpenUploadModal = () => {
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    // Reset form fields on close
    setFlightName('');
    setFlightLocation('');
    setSelectedFiles([]); // Reset multiple files
  };

  const handleFileChange = (event) => {
    setSelectedFiles(Array.from(event.target.files)); // Store all selected files as an array
  };

  const GENERATE_UPLOAD_URL_API_ENDPOINT = process.env.NODE_ENV === 'production'
  ? 'https://kjzma4p2jc.execute-api.eu-west-2.amazonaws.com/prod/generate-upload-url'
  : '/generate-upload-url';

  const LOG_FLIGHT_DETAILS_API_ENDPOINT = process.env.NODE_ENV === 'production'
  ? 'https://4ejzubuzqd.execute-api.eu-west-2.amazonaws.com/prod/writeFlightLogDetails' 
  : '/writeFlightLogDetails';

  const handleFlightUpload = async () => {
    if (!flightName || !flightLocation || selectedFiles.length === 0) {
      alert('Please fill in flight name, location, and select at least one .ulg file.');
      return;
    }

    let allFilesValid = true;
    const filesToUpload = [];

    selectedFiles.forEach(file => {
      if (!file.name.toLowerCase().endsWith('.ulg')) {
        alert(`Invalid file type: ${file.name}. Only .ulg files are allowed.`);
        allFilesValid = false;
        return; // Skip this file, or could stop entire upload
      }

      const newUuid = crypto.randomUUID();
      const newFileName = `${newUuid}.ulg`;
      
      filesToUpload.push({
        originalFile: file,
        s3FileName: newFileName,
        flightName: flightName,
        flightLocation: flightLocation
      });
    });

    if (!allFilesValid) {
      // Optionally clear selected files if any were invalid, or let user re-select
      // setSelectedFiles([]); 
      return; // Stop if any file was invalid
    }

    if (filesToUpload.length === 0) {
        alert('No valid .ulg files were selected for upload.');
        return;
    }

    // Upload each file using the presigned URL method
    for (const uploadData of filesToUpload) {
      try {
        // 1. Get a presigned URL from the backend
        console.log(`Requesting upload URL for ${uploadData.s3FileName}...`);
        const presignedUrlResponse = await authFetch(GENERATE_UPLOAD_URL_API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fileName: uploadData.s3FileName,
            contentType: uploadData.originalFile.type || 'application/octet-stream',
          }),
        });

        if (!presignedUrlResponse.ok) {
          const errorData = await presignedUrlResponse.json().catch(() => ({}));
          throw new Error(`Failed to get an upload URL: ${errorData.message || presignedUrlResponse.statusText}`);
        }

        const { uploadUrl, key: s3ObjectKey } = await presignedUrlResponse.json();

        // 2. Upload the file directly to S3 using the presigned URL
        console.log(`Uploading ${uploadData.originalFile.name} to S3...`);
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: uploadData.originalFile,
          headers: {
            'Content-Type': uploadData.originalFile.type || 'application/octet-stream',
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
        }

        console.log(`Successfully uploaded ${uploadData.originalFile.name} to S3 as ${s3ObjectKey}.`);

        // 3. Log flight details to the database
        const account = accounts[0]; // Assuming one account is active
        let flightUuid = '';
        // Extract UUID from s3FileName (e.g., "<uuid>.ulg")
        if (uploadData.s3FileName && uploadData.s3FileName.endsWith('.ulg')) {
          flightUuid = uploadData.s3FileName.substring(0, uploadData.s3FileName.length - 4);
        }

        const flightDetailsPayload = {
          flight_name: uploadData.flightName,
          flight_location: uploadData.flightLocation,
          s3_object_key: s3ObjectKey, // Use the key returned from presigned URL generation
          flight_uuid: flightUuid, 
          uploader_name: account ? account.name : 'Unknown',
          uploader_email: account ? account.username : 'Unknown', // MSAL username is often the email
          uploader_oid: account && account.idTokenClaims ? account.idTokenClaims.oid : 'Unknown',
        };

        try {
          console.log(`Logging flight details for ${uploadData.originalFile.name}:`, flightDetailsPayload);
          const logResponse = await authFetch(LOG_FLIGHT_DETAILS_API_ENDPOINT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(flightDetailsPayload),
          });

          if (!logResponse.ok) {
            const errorData = await logResponse.json().catch(() => ({}));
            throw new Error(`Failed to log flight details: ${errorData.message || logResponse.statusText}`);
          }
          console.log(`Successfully logged flight details for ${uploadData.originalFile.name}`);
          alert(`Successfully uploaded and logged details for ${uploadData.originalFile.name}.`);

        } catch (dbError) {
          if (isInteractiveAuthError(dbError)) return;
          console.error(`Failed to log flight details for ${uploadData.originalFile.name}:`, dbError);
          // Decide if this is a critical error. For now, alert and continue.
          alert(`File ${uploadData.originalFile.name} uploaded, but failed to log details: ${dbError.message}.`);
        }

      } catch (error) {
        if (isInteractiveAuthError(error)) return;
        console.error(`Failed to upload ${uploadData.originalFile.name}:`, error);
        alert(`Error uploading ${uploadData.originalFile.name}: ${error.message}.`);
        // Stop the entire upload process if one file fails
        return;
      }
    }

    console.log(`${filesToUpload.length} file(s) successfully uploaded.`);
    handleCloseUploadModal(); // Close modal after 'upload'
  };

  const handleDownload = async () => {
    try {
      // Replace '/api/download-data' with your actual API Gateway endpoint for the Lambda
      const response = await authFetch('https://vrw0nl99e2.execute-api.eu-west-2.amazonaws.com/prod/readFlightLogSummaryData'); // Or use axios: await axios.get('/api/download-data', { responseType: 'blob' });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const filename = response.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') || 'data.csv';
      
      // Create a link element, click it to trigger download, then remove it
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', filename); // Or a fixed name like 'flight_data.csv'
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

    } catch (error) {
      if (isInteractiveAuthError(error)) return;
      console.error("Download failed:", error);
      // Handle error appropriately in your UI, e.g., show a notification
      alert("Failed to download data. Please check the console for more details.");
    }
  };

  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalContentStyle = {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: 0,
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    width: '400px',
    textAlign: 'left',
  };

  const inputStyle = {
    width: 'calc(100% - 20px)',
    padding: '10px',
    marginBottom: '15px',
    border: '1px solid #ccc',
    borderRadius: 0,
    fontSize: '16px',
  };
  
  const buttonStyle = {
    padding: '10px 20px',
    fontSize: '16px',
    cursor: 'pointer',
    border: 'none',
    borderRadius: 0,
    marginRight: '10px',
  };

  return (
    <div style={{ position: 'relative', padding: '20px' }}>
      <div style={{ position: 'fixed', top: '80px', left: '20px', display: 'flex', gap: '10px', zIndex: 1001 }}>
        <button 
          onClick={handleOpenUploadModal} 
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            cursor: 'pointer',
            backgroundColor: 'black',
            color: 'white',
            border: 'none',
            borderRadius: '0px'
          }}
        >
          Upload
        </button>
        <button 
          onClick={handleDownload} 
          style={{ 
            padding: '10px 15px',
            fontSize: '16px',
            cursor: 'pointer',
            backgroundColor: 'black',
            color: 'white',
            border: 'none',
            borderRadius: '0px'
          }}
        >
          Download
        </button>
      </div>

          
      {showUploadModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h2 style={{ marginBottom: '20px', textAlign: 'center' }}>Upload ULog File</h2>
            <label htmlFor="flightName">Name:</label>
            <input 
              type="text" 
              id="flightName" 
              value={flightName} 
              onChange={(e) => setFlightName(e.target.value)} 
              style={inputStyle} 
              placeholder="e.g., Morning Test Flight"
            />
            
            <label htmlFor="flightLocation">Location:</label>
            <input 
              type="text" 
              id="flightLocation" 
              value={flightLocation} 
              onChange={(e) => setFlightLocation(e.target.value)} 
              style={inputStyle} 
              placeholder="e.g., Local Airfield"
            />
            
            <label htmlFor="ulgFile">ULog File (.ulg):</label>
            <input 
              type="file" 
              id="ulgFile" 
              accept=".ulg" 
              multiple // Allow multiple file selection
              onChange={handleFileChange} 
              style={{...inputStyle, padding: '10px 0'}}
            />
            
            <div style={{ marginTop: '25px', textAlign: 'right' }}>
              <button onClick={handleCloseUploadModal} style={{...buttonStyle, backgroundColor: '#6c757d', color: 'white'}}>
                Cancel
              </button>
              <button onClick={handleFlightUpload} style={{...buttonStyle, backgroundColor: '#28a745', color: 'white'}}>
                Upload Flight
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Visualise;
